import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  map,
  range,
  paths,
  t,
  styles,
  classMap,
} from '@hh/global.js'

class HHSlideshow extends LitElement {
  static properties = {
    _current: { state: true },
    _total: { state: true },
    speed: { reflect: true, type: Number },
    playing: { reflect: true, type: Boolean },
    userPause: { type: Boolean },
    autoplay: { state: true },
    slideCount: { type: Number, attribute: 'slide-count' },
    hideArrows: { type: Boolean, attribute: 'hide-arrows' },
    strings: { type: Object },
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()
  $togglePlay = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1
    this.playing = false
    this.userPause = false
    this.speed = 0
    this.highlightCompleted = false

    this.touchstartX = 0
    this.touchendX = 0
    this.swipeThreshold = 50

    this.updateUI = this.updateUI.bind(this)
    this._boundWindowKeydown = (e) => this.onModalKeydown(e)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length

    if (this.slideCount == null || Number.isNaN(this.slideCount)) {
      this.slideCount = this._total
    }

    this.hideArrows = this.hasAttribute('hide-arrows')

    const speedAttr = this.getAttribute('speed')
    this.speed = speedAttr ? Number(speedAttr) : 0

    const stringsAttr = this.getAttribute('strings')
    if (!this.strings || typeof this.strings !== 'object') {
      if (stringsAttr) {
        try {
          this.strings = JSON.parse(stringsAttr)
        } catch (_) {
          this.strings = {}
        }
      } else {
        this.strings = {}
      }
    }

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

    if (!this.reducedMotion.matches && this.hasAttribute('use-video-rotation')) {
      this.setupVideoEndTrigger(this.items[this._current - 1])
    } else if (this.hasAttribute('speed')) {
      this.setAutoplay()
    } else {
      this.autoplay = false
      this.userPause = true
    }

    if (this.hasAttribute('use-video-rotation')) {
      const initialVideoSlide = this.items[this._current - 1]
      this.setupVideoEndTrigger(initialVideoSlide)
    }

    window.addEventListener('keydown', this._boundWindowKeydown)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    const currentSlide = this.items?.[this._current - 1]
    const video = currentSlide?.querySelector?.('video')
    if (video) video.onended = null

    clearTimeout(this.imageSlideTimeout)
    clearInterval(this.autoplay)

    window.removeEventListener('keydown', this._boundWindowKeydown)
  }

  firstUpdated() {
    const c = this.$container?.value
    if (!c) return

    c.addEventListener('scroll', this.updateUI, { passive: true })
    c.addEventListener('touchstart', this.onTouchStart, { passive: true })
    c.addEventListener('touchend', this.onTouchEnd, { passive: true })

    this.setSlideVisibility()
    this.updateUI()
  }

  updated(changedProperties) {
    if (changedProperties.has('_current')) {
      this.setSlideVisibility()
    }
  }

  setSlideVisibility() {
    if (!this.items?.length) return

    this.items.forEach((item, idx) => {
      const linkElements = item.querySelectorAll('hh-button')
      const idxOffset = idx + 1

      if (idxOffset === this._current) {
        if (linkElements.length) {
          linkElements.forEach((button) => button.removeAttribute('tabindex'))
        }
        item.setAttribute('aria-hidden', 'false')
        item.removeAttribute('tabindex')
      } else {
        if (linkElements.length) {
          linkElements.forEach((button) => button.setAttribute('tabindex', '-1'))
        }
        item.setAttribute('aria-hidden', 'true')
        item.setAttribute('tabindex', '-1')
      }
    })
  }

  updateUI() {
    if (!this._total || !this.$container?.value || !this.items?.[0]) return

    const c = this.$container.value
    const slideWidth =
      this.items[0].getBoundingClientRect().width || c.getBoundingClientRect().width

    if (!slideWidth) return

    const rawIndex = Math.round(c.scrollLeft / slideWidth) + 1
    const newIndex = Math.min(Math.max(rawIndex, 1), this._total)

    if (newIndex !== this._current) {
      this._current = newIndex

      const currentSlide = this.items[this._current - 1]
      const video = currentSlide?.querySelector?.('video')

      if (video) {
        if (video.ended) {
          try {
            video.currentTime = 0
            video.play().catch(() => {})
          } catch (_) {}
        }

        if (this.hasAttribute('use-video-rotation')) {
          this.setupVideoEndTrigger(currentSlide)
        }
      }
    }

    this._current === 1
      ? this.$prev?.value?.setAttribute('disabled', true)
      : this.$prev?.value?.removeAttribute('disabled')

    this._current === this._total
      ? this.$next?.value?.setAttribute('disabled', true)
      : this.$next?.value?.removeAttribute('disabled')
  }

  move = (event) => {
    event.preventDefault()
    this.userPause = true
    this.stopAutoplay()

    const dir = event?.currentTarget?.name
    const next =
      dir === 'next'
        ? Math.min(this._current + 1, this._total)
        : Math.max(this._current - 1, 1)

    this.moveTo(next - 1)
  }

  moveTo(slide) {
    if (!this.items?.[slide] || !this.$container?.value) return

    const slideScrollPosition = this.items[slide].offsetLeft
    this.$container.value.scrollTo({
      left: slideScrollPosition,
      behavior: 'smooth',
    })

    const nextSlide = this.items[slide]

    const video = nextSlide.querySelector('video')
    if (video) {
      try {
        video.currentTime = 0
        video.play().catch(() => {})
      } catch (_) {}
    }

    const nextSlideImages = nextSlide.querySelectorAll('img')
    this.eagerLoadSlidesImages(nextSlideImages)

    this.highlightCompleted = false
    this.requestUpdate()
  }

  eagerLoadSlidesImages(images) {
    images.forEach((img) => img.setAttribute('loading', 'eager'))
  }

  setupVideoEndTrigger(slide) {
    if (!this.hasAttribute('use-video-rotation')) return

    clearTimeout(this.imageSlideTimeout)
    this.playing = true
    this.userPause = false

    const videos = slide.querySelectorAll('video')

    if (videos.length > 0) {
      videos.forEach((video) => {
        video.onended = null

        const updateSpeedFromVideo = () => {
          const duration = video.duration || 5
          this.style.setProperty('--slideshow-speed', `${duration}s`)
        }

        video.addEventListener('loadedmetadata', updateSpeedFromVideo, { once: true })

        try {
          video.currentTime = 0
          video.play().catch(() => {})
        } catch (_) {}

        video.onended = () => {
          this.highlightCompleted = true
          this.requestUpdate()
          this.rotateSlides()
        }
      })
    } else {
      const imageDelay = Number.parseInt(this.getAttribute('speed'), 10) || 5000
      this.style.setProperty('--slideshow-speed', `${imageDelay / 1000}s`)

      this.imageSlideTimeout = setTimeout(() => {
        this.highlightCompleted = true
        this.requestUpdate()
        this.rotateSlides()
      }, imageDelay)
    }
  }

  rotateSlides() {
    clearTimeout(this.imageSlideTimeout)

    const currentSlide = this.items[this._current - 1]
    const oldVideos = currentSlide?.querySelectorAll?.('video') || []
    oldVideos.forEach((video) => (video.onended = null))

    const isLastSlide = this._current === this._total
    const nextIndex = isLastSlide ? 0 : this._current

    this.moveTo(nextIndex)
    this.setupVideoEndTrigger(this.items[nextIndex])
  }

  play = () => {
    this.$container?.value?.setAttribute('aria-live', 'off')
    this.playing = true
  }

  pause = () => {
    this.$container?.value?.setAttribute('aria-live', 'polite')
    this.playing = false
  }

  setAutoplay() {
    if (!this.speed || this.hasAttribute('use-video-rotation')) return

    this.style.setProperty('--slideshow-speed', `${this.speed / 1000}s`)

    clearInterval(this.autoplay)
    this.autoplay = setInterval(() => this.rotateSlides(), this.speed)
    this.play()
  }

  stopAutoplay() {
    clearInterval(this.autoplay)
    this.pause()
  }

  toggleAutoplay = () => {
    if (this.hasAttribute('use-video-rotation')) return

    if (this.playing) {
      this.userPause = true
      this.stopAutoplay()
    } else {
      this.userPause = false
      this.setAutoplay()
    }
  }

  onPaginationClick = (e, slideIndex) => {
    e.preventDefault()
    this.userPause = true
    this.stopAutoplay()
    this.moveTo(slideIndex)
  }

  onTouchStart = (e) => {
    this.touchstartX = e.changedTouches[0].screenX
  }

  onTouchEnd = (e) => {
    this.touchendX = e.changedTouches[0].screenX
    this.checkSwipe()
  }

  checkSwipe() {
    if (
      this.touchendX < this.touchstartX - this.swipeThreshold ||
      this.touchendX > this.touchstartX + this.swipeThreshold
    ) {
      this.userPause = true
      this.stopAutoplay()
    }
  }

  onVideoLinkClick = (e) => {
    e.preventDefault()

    if (typeof scrollLock !== 'undefined') {
      scrollLock.disablePageScroll(this.videoModal)
    }

    const videoUrl = e.target.getAttribute('video-url')
    const videoPoster = e.target.getAttribute('video-poster')
    const youtubeId = e.target.getAttribute('youtube-id')

    this.userPause = true
    this.stopAutoplay()

    if (videoUrl) {
      this.videoModal.innerHTML = this.vdsTemplate(videoUrl, videoPoster)
      const videoHTML = this.videoModal.querySelector('video')
      videoHTML.addEventListener('canplay', () => {
        videoHTML.play().catch(() => {})
      })
    } else if (youtubeId) {
      this.videoModal.innerHTML = this.youtubeTemplate(youtubeId)
      const youtubeHTML = this.videoModal.querySelector('iframe')
      youtubeHTML.addEventListener('load', () => {
        youtubeHTML.contentWindow.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          '*'
        )
      })
    }
  }

  onModalClose = () => {
    if (typeof scrollLock !== 'undefined') {
      scrollLock.enablePageScroll(this.videoModal)
    }
    if (this.videoModal) this.videoModal.innerHTML = ''
    this.userPause = false
    this.setAutoplay()
  }

  onModalClickOutside = (e) => {
    if (e.target !== this.hhModal) return
    this.hhModal.close(e)
    this.onModalClose()
  }

  onModalKeydown(e) {
    if (e.code.toUpperCase() !== 'ESCAPE') return
    this.hhModal.close(e)
    this.onModalClose()
  }

  get videoModal() {
    return this.querySelector('.js-video-modal') ?? null
  }

  get hhModal() {
    return this.querySelector('hh-modal') ?? null
  }

  vdsTemplate(source, poster = '') {
    if (poster) {
      return `<video controls playsinline src="${source}" poster="${poster}"></video>`
    }
    return `<video controls playsinline src="${source}"></video>`
  }

  youtubeTemplate(id) {
    return `
      <iframe
        src="https://www.youtube.com/embed/${id}?enablejsapi=1&playlist=${id}&disablekb=0&fs=1&iv_load_policy=1&modestbranding=1&rel=1&widgetid=1"
        frameborder="0"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowfullscreen
      ></iframe>
    `
  }

  render() {
    return html`
      <div>
        <div
          class="container"
          ${ref(this.$container)}
        >
          <slot name="item" @openVideoModal=${this.onVideoLinkClick}></slot>
        </div>

        <div class="controls">
          <div
            class="${classMap({
              pagination: true,
              hidden: this.slideCount === 1,
            })}"
          >
            ${map(range(this.slideCount), (idx) => {
              return html`
                <button
                  class="pagination-button"
                  aria-label="Go to slide ${idx + 1}"
                  aria-current="${this._current === idx + 1}"
                  @click=${(e) => this.onPaginationClick(e, idx)}
                >
                  <span class="pagination-button-inner">
                    <span
                      data-paginate-progress
                      class="pagination-button-progress ${classMap({
                        animation: this._current === idx + 1 && !this.userPause,
                        pause: !this.playing,
                        selected: this._current === idx + 1 && this.userPause,
                        completed: this.highlightCompleted && this._current > idx + 1,
                      })}"
                    ></span>
                  </span>
                </button>
              `
            })}
          </div>

          <button
            class="${classMap({
              toggle: true,
              hidden: !this.speed || this.slideCount === 1,
            })}"
            @click=${this.toggleAutoplay}
            aria-label=${this.playing ? this.strings.pause : this.strings.play}
          >
            <span class="${classMap({ hidden: this.playing })}"
              >${unsafeHTML(paths.play)}</span
            >
            <span class="${classMap({ hidden: !this.playing })}"
              >${unsafeHTML(paths.pause)}</span
            >
          </button>
        </div>

        <div
          class="${classMap({
            buttons: true,
            hidden: this.slideCount === 1 || this.hideArrows,
          })}"
        >
          <button
            ${ref(this.$prev)}
            @click=${this.move}
            type="button"
            class="button button--prev slideshow-button"
            name="previous"
            aria-label="${t('accessibility.previous_slide', 'Previous')}"
          >
            ${unsafeHTML(paths.caret)}
          </button>
          <button
            ${ref(this.$next)}
            @click=${this.move}
            type="button"
            class="button button--next slideshow-button"
            name="next"
            aria-label="${t('accessibility.next_slide', 'Next')}"
          >
            ${unsafeHTML(paths.caret)}
          </button>
        </div>
      </div>

      <slot
        name="video-modal"
        @modal-close=${this.onModalClose}
        @click=${this.onModalClickOutside}
        @keydown=${this.onModalKeydown}
      ></slot>
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host {
        box-sizing: border-box;
        position: relative;
        overflow: hidden;
        display: block;
      }

      *,
      *::before,
      *::after {
        box-sizing: inherit;
      }

      .container {
        width: var(--hh-container-width, 100%);
        margin: var(--hh-container-margin);
        display: flex;
        flex-wrap: nowrap;
        position: relative;
        background: var(--hh-background);
        overflow-x: auto;
        overflow-y: hidden;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
        touch-action: pan-x;
      }

      .container::-webkit-scrollbar {
        display: none;
      }

      ::slotted([slot='item']) {
        flex: 0 0 100% !important;
        width: 100% !important;
        scroll-snap-align: start;
        scroll-snap-stop: always;
      }

      .controls {
        position: absolute;
        padding: 0 1rem;
        bottom: 0;
        display: flex;
        align-items: baseline;
        column-gap: 1rem;
        width: calc(100% - 2rem);
      }

      .pagination {
        display: flex;
        width: 100%;
        justify-content: space-between;
        column-gap: 1rem;
      }

      .toggle {
        width: 2.4rem;
        height: 2.4rem;
        padding: 0;
        color: var(--hh-color-background);
        background-color: transparent;
        border: none;
        cursor: pointer;
      }

      .toggle svg {
        width: 100%;
        height: 100%;
      }

      .buttons {
        display: none;
        align-items: center;
        justify-content: space-between;
        position: absolute;
        z-index: 2;
        top: 50%;
        left: 0;
        right: 0;
        transform: translateY(-50%);
        padding: 0 1rem;
        pointer-events: none;
      }

      .hidden {
        display: none;
      }

      .button {
        pointer-events: auto;
        width: 36px;
        height: 36px;
        cursor: pointer;
        color: var(--hh-color-night, #000);
        border: none;
        background: transparent;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s ease, opacity 0.2s ease;
      }

      .button:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }

      .button svg {
        width: 8px;
        height: auto;
      }

      .button--prev {
        transform: rotate(90deg);
      }

      .button--next {
        transform: rotate(270deg);
      }

      @media (min-width: 749px) {
        .buttons {
          display: flex;
          padding: 0 50px;
        }

        .button {
          width: 48px;
          height: 48px;
        }

        .button svg {
          width: 12px;
        }
      }

      .pagination-button {
        width: 100%;
        padding: 2rem 0 1rem;
        background: transparent;
        border: none;
        cursor: pointer;
      }

      .pagination-button-inner {
        position: relative;
        display: block;
        background: rgba(255, 255, 255, 0.7);
        height: 0.2rem;
      }

      .pagination-button-progress {
        position: absolute;
        inset: 0;
        display: block;
        background: red;
        height: 0.2rem;
        width: 0;
      }

      .pagination-button-progress.animation {
        animation: slideProgress var(--slideshow-speed) linear 1 forwards;
      }

      .pagination-button-progress.completed,
      .pagination-button-progress.selected {
        width: 100%;
      }

      .pagination-button-progress.pause {
        animation-play-state: paused;
      }

      @keyframes slideProgress {
        from {
          width: 0;
        }
        to {
          width: 100%;
        }
      }
    `,
  ]
}

customElements.define('hh-slideshow', HHSlideshow)